import axios from 'axios';
import {
    LoginData,
    RegisterData,
    AuthResponse,
    Conversation,
    FileUploadResponse,
    UserProfileUpdateData,
    ChatStatsResponse,
    RefreshTokenResponse,
    LinkPreviewData,
    MetaConversations, FilesSharedResponse, GetUsersResponse
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

let isRefreshing = false;
let failedQueue: { resolve: (value: unknown) => void; reject: (reason?: any) => void; }[] = [];

const processQueue = (error: unknown, token: string) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken');

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('theme');
    window.location.href = '/login';
}

const notifyTokenRefresh = (accessToken: string, refreshToken: string) => {
    const event = new CustomEvent('token-refreshed', {
        detail: { accessToken, refreshToken }
    });
    window.dispatchEvent(event);
};

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (
            error.response?.status !== 401 ||
            originalRequest.url === '/auth/login' ||
            originalRequest.url === '/auth/refresh-token' ||
            originalRequest._retry
        ) {
            if (error.response?.status === 401 && error.config.url !== '/auth/login') {
                handleLogout();
            }
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            })
                .then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return api(originalRequest);
                })
                .catch(err => {
                    return Promise.reject(err);
                });
        }

        isRefreshing = true;

        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
            handleLogout();
            return Promise.reject(error);
        }

        try {
            const oldAccessToken = localStorage.getItem('accessToken') || '';

            const response = await authAPI.refreshToken({ refreshToken, oldAccessToken });

            const { accessToken, refreshToken: newRefreshToken } = response;

            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', newRefreshToken);

            notifyTokenRefresh(accessToken, newRefreshToken);

            originalRequest.headers.Authorization = `Bearer ${accessToken}`;

            processQueue(null, accessToken);

            return api(originalRequest);

        } catch (refreshError) {
            processQueue(refreshError, '');
            handleLogout();
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
);

export const authAPI = {
  login: (data: LoginData): Promise<AuthResponse> => 
    api.post('/auth/login', data).then(res => res.data),

  refreshToken: (data: {refreshToken: string, oldAccessToken: string}): Promise<RefreshTokenResponse> =>
    api.post('/auth/refresh-token', data).then(res => res.data),

  logout: (): Promise<{ message: string }> =>
    api.post('/auth/logout').then(res => res.data),
  
  register: (data: RegisterData): Promise<{ message: string }> => 
    api.post('/auth/register', data).then(res => res.data),

  verifyEmail: (token: string) : Promise<{ message: string,  email: string}> =>
     api.get(`/auth/verify-email/${token}`).then(res => res.data),

  resendVerification: (email: string) =>
     api.post('/auth/resend-verification', { email }).then(res => res.data),
  
  forgotPassword: (email: string): Promise<{ message: string }> => 
    api.post('/auth/forgot-password', { email }).then(res => res.data),
  
  resetPassword: (token: string, newPassword: string): Promise<{ message: string }> => 
    api.post('/auth/reset-password', { token, newPassword }).then(res => res.data),
  
  getProfile: (): Promise<AuthResponse> => 
    api.get('/auth/profile').then(res => res.data),
  
  updateProfile: (data: UserProfileUpdateData): Promise<AuthResponse> => 
    api.put('/auth/profile', data).then(res => res.data),
  
  changePassword: (data: { current_password: string; new_password: string }): Promise<{ message: string }> => 
    api.put('/auth/change-password', data).then(res => res.data),
  
  uploadAvatar: (formData: FormData): Promise<FileUploadResponse> => 
    api.post('/auth/upload-avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data),
};

export const chatAPI = {
  getPreviewLink: (url: string): Promise<LinkPreviewData> =>
    api.get('/chat/preview', { params: { url } }).then(res => res.data),

  // getMessages: (conversationId: number): Promise<Message[]> =>
  getMessages: (conversationId: number, limit: number, cursorCreatedAt?: string | null, cursorId?: number | null, direction?: string): Promise<any> =>
    api.get(`/chat/messages/${conversationId}`, { params: { limit, cursorCreatedAt, cursorId, direction } }).then(res => res.data),

  searchMessages: (conversationId: number, limit: number, offset: number, q: string): Promise<any> =>
    api.get(`/chat/conversations/${conversationId}/search`, { params: { limit, offset, q } }).then(res => res.data),

  getMessagesAround: (conversationId: number, messageId: number, limit: number = 50): Promise<any> =>
      api.get(`/chat/conversations/${conversationId}/messages/around/${messageId}`, { params: { limit } }).then(res => res.data),

  uploadFile: (file: File): Promise<FileUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/chat/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data);
  },

  downloadFile: async (path: string): Promise<Blob> => {
      const res = await api.get(`/chat/download/${path}`,
          {
              responseType: 'blob',
              headers: { 'Content-Type': 'application/octet-stream' }
          }
      );
      return res.data;
  },

  downloadFilesAsZip: async (paths: string[], filenames?: string[]): Promise<Blob> => {
      const res = await api.post(`/chat/download-zip-stream`,
          {paths, filenames},
          {
              responseType: 'blob',
              headers: { 'Content-Type': 'application/json', }
          },
      )
      return res.data;
  },

  createConversation: (): Promise<{ conversationId: number, isCreateNew: boolean, user_info_submitted: boolean }> =>
    api.post('/chat/conversations').then(res => res.data),

  getFilesShared: ({ conversationId, type = 'media', limit = 10, offset = 0 } : { conversationId: string, type?: string, limit?: number, offset?: number }): Promise<FilesSharedResponse> =>
    api.get(`/chat/conversations/${conversationId}/files`, { params: { type, limit, offset } }).then(res => res.data),

  submitUserInfo: async (
      conversationId: number,
      userInfo: {
          lastName: string;
          firstName: string;
          companyName: string;
          address: string;
          phone: string;
          // supportRequest: string;
      }
  ) => {
      try {
          const response = await api.post(
              `/chat/conversations/${conversationId}/user-info`,
              { ...userInfo },
          );
          return response.data;
      } catch (error) {
          console.error('Submit user info error:', error);
          throw error;
      }
  },
};

export const adminAPI = {
    getUsers: (params?: {
        page?: number;
        limit?: number;
        search?: string;
    }): Promise<GetUsersResponse> =>
        api.get('/admin/users', { params }).then(res => res.data),

  createUser: (data: { full_name: string; email: string; password: string; role: string }): Promise<{ message: string }> =>
    api.post('/admin/users', data).then(res => res.data),

  updateUserRole: (userId: number, role: string): Promise<{ message: string, user: any }> =>
      api.put(`/admin/users/${userId}/role`, { role }).then(res => res.data),

    updateUserNickname: (userId: number, name: string | null): Promise<{ message: string, user: any }> =>
      api.put(`/admin/users/${userId}/nickname`, { nickname: name }).then(res => res.data),

  updateUserStatus: (userId: number, isActive: boolean): Promise<{ message: string }> => 
    api.put(`/admin/users/${userId}/status`, { is_active: isActive }).then(res => res.data),
  
  getConversations: (offset: number = 0, limit: number = 20, search: string = ''): Promise<{ conversations: Conversation[], pagination: MetaConversations }> =>
    api.get('/admin/conversations', { params: { offset, limit, ...search && {search} } }).then(res => res.data),

  getConversation: (conversationId: number): Promise<Conversation> =>
    api.get(`/admin/conversations/${conversationId}`).then(res => res.data),

  getChatStats: (conversationId: number): Promise<ChatStatsResponse> =>
      api.get(`admin/conversations/${conversationId}/response-time`).then(res => res.data),
};

export default api;
